import React from "react";

import founderHeadshot from "assets/img/faces/ryan-headshot.jpg";
import SiteAction from "components/site/SiteAction";
import PageHero from "components/site/PageHero";
import RevealBlock from "components/site/RevealBlock";
import { Col, Container, Row } from "components/site/SitePrimitives";
import SiteShell from "components/site/SiteShell";
import { buildStructuredData, homePage } from "content/siteContent";
import usePageMeta from "hooks/usePageMeta";

function Index() {
  usePageMeta({
    ...homePage.meta,
    structuredData: buildStructuredData({
      pageName: "Home",
      description: homePage.meta.description,
      path: homePage.meta.path,
    }),
  });

  const handleScrollToPositioning = () => {
    const section = document.getElementById("home-positioning");

    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <SiteShell pageClassName="ignitum-site-page ignitum-home-page">
      <section className="site-home-splash">
        <Container className="site-home-splash-inner">
          <img
            alt="Ignitum Solutions"
            className="site-home-splash-logo"
            src="/logos/IgnitumSolutions_RGB_Horizontal.svg"
          />
          <p className="site-home-splash-tagline">{homePage.splash.tagline}</p>
          <button
            className="site-home-splash-link"
            onClick={handleScrollToPositioning}
            type="button"
          >
            {homePage.splash.actionLabel}
          </button>
        </Container>
      </section>

      <PageHero
        actions={homePage.hero.actions}
        aside={
          <>
            <p className="site-panel-label">{homePage.hero.signalTitle}</p>
            <ul className="site-signal-list">
              {homePage.hero.signals.map((signal) => (
                <li key={signal}>{signal}</li>
              ))}
            </ul>
            <p className="site-panel-note">{homePage.hero.signalNote}</p>
          </>
        }
        description={homePage.hero.description}
        eyebrow={homePage.hero.eyebrow}
        heroClassName="site-home-positioning"
        sectionId="home-positioning"
        title={homePage.hero.title}
      />

      <section className="site-section">
        <Container>
          <RevealBlock className="site-section-intro" variant="lift">
            <p className="site-eyebrow">{homePage.focusAreas.eyebrow}</p>
            <h2>{homePage.focusAreas.title}</h2>
            <p>{homePage.focusAreas.description}</p>
          </RevealBlock>
          <Row className="site-card-grid">
            {homePage.focusAreas.items.map((item, index) => (
              <Col key={item.title} md="6">
                <RevealBlock delay={index * 70} variant="card">
                  <div className="site-card site-home-focus-card">
                    <p className="site-card-label">{item.label}</p>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    {item.points?.length ? (
                      <div className="site-tag-row">
                        {item.points.map((point) => (
                          <span className="site-tag" key={point}>
                            {point}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </RevealBlock>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="site-section site-section-muted">
        <Container>
          <RevealBlock className="site-section-intro" variant="lift">
            <p className="site-eyebrow">{homePage.currentDirection.eyebrow}</p>
            <h2>{homePage.currentDirection.title}</h2>
            <p>{homePage.currentDirection.description}</p>
          </RevealBlock>
          <Row className="site-card-grid">
            <Col lg="6">
              <RevealBlock variant="slide-left">
                <div className="site-card site-highlight">
                  <h3>{homePage.currentDirection.activeNow.title}</h3>
                  <ul className="site-signal-list">
                    {homePage.currentDirection.activeNow.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </RevealBlock>
            </Col>
            <Col lg="6">
              <RevealBlock delay={80} variant="slide-right">
                <div className="site-card">
                  <h3>{homePage.currentDirection.inProgress.title}</h3>
                  <ul className="site-signal-list">
                    {homePage.currentDirection.inProgress.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </RevealBlock>
            </Col>
          </Row>
          {homePage.currentDirection.actions.length ? (
            <div className="site-page-actions">
              {homePage.currentDirection.actions.map((action) => (
                <SiteAction
                  className={`site-btn ${
                    action.variant === "primary"
                      ? "site-btn-primary"
                      : "site-btn-secondary"
                  }`}
                  key={action.label}
                  to={action.to}
                >
                  {action.label}
                </SiteAction>
              ))}
            </div>
          ) : null}
        </Container>
      </section>

      <section className="site-section">
        <Container>
          <Row className="site-founder-layout site-grid-gap">
            <Col lg="5">
              <RevealBlock className="site-founder-media site-founder-media-home" variant="slide-left">
                <img
                  alt="Ryan Carlisle, founder of Ignitum Solutions"
                  src={founderHeadshot}
                />
              </RevealBlock>
            </Col>
            <Col lg="7">
              <RevealBlock variant="slide-right">
                <div className="site-section-intro mb-0">
                  <p className="site-eyebrow">{homePage.founder.eyebrow}</p>
                  <h2>{homePage.founder.title}</h2>
                  <p>{homePage.founder.description}</p>
                </div>
                <div className="site-text-block">
                  {homePage.founder.body.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
                <div className="site-badge-row">
                  {homePage.founder.badges.map((badge) => (
                    <span className="site-badge" key={badge}>
                      {badge}
                    </span>
                  ))}
                </div>
                {homePage.founder.actions.length ? (
                  <div className="site-page-actions">
                    {homePage.founder.actions.map((action) => (
                      <SiteAction
                        className="site-btn site-btn-secondary"
                        key={action.label}
                        to={action.to}
                      >
                        {action.label}
                      </SiteAction>
                    ))}
                  </div>
                ) : null}
              </RevealBlock>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="site-section site-section-muted">
        <Container>
          <RevealBlock variant="card">
            <div className="site-card site-highlight">
              <div className="site-section-intro mb-0">
                <p className="site-eyebrow">{homePage.conversation.eyebrow}</p>
                <h2>{homePage.conversation.title}</h2>
                <p>{homePage.conversation.description}</p>
              </div>
              <div className="site-page-actions">
                {homePage.conversation.actions.map((action) =>
                  action.external ? (
                    <SiteAction
                      className={`site-btn ${
                        action.variant === "primary"
                          ? "site-btn-primary"
                          : "site-btn-secondary"
                      }`}
                      external
                      key={action.label}
                      to={action.to}
                    >
                      {action.label}
                    </SiteAction>
                  ) : (
                    <SiteAction
                      className={`site-btn ${
                        action.variant === "primary"
                          ? "site-btn-primary"
                          : "site-btn-secondary"
                      }`}
                      key={action.label}
                      to={action.to}
                    >
                      {action.label}
                    </SiteAction>
                  )
                )}
              </div>
            </div>
          </RevealBlock>
        </Container>
      </section>
    </SiteShell>
  );
}

export default Index;
